import { useEffect, useRef } from 'react';

export type AppNotification = {
  id: string;
  type: 'space_request' | 'friend_request' | 'profile_photo' | 'activity';
  title: string;
  body?: string;
  createdAt: number;
  unread: boolean;
  userId?: string;
  spaceId?: string;
  requestId?: string;
  avatarUrl?: string | null;
  actions?: {
    label: string;
    style?: 'primary' | 'danger';
    onClick: () => void;
  }[];
  onOpen?: () => void;
};

export default function NotificationCenter({
  open,
  items,
  onClose,
  onMarkAllRead,
  onDismiss,
}: {
  open: boolean;
  items: AppNotification[];
  onClose: () => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      const t = e.target as Node | null;
      if (!ref.current || !t) return;
      if (!ref.current.contains(t)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div ref={ref} className="fixed bottom-6 left-4 md:left-[5rem] md:translate-x-[320px] z-[6500] w-[320px] max-w-[92vw]">
      <div className="rounded-xl overflow-hidden border border-neutral-800 bg-neutral-950 shadow-2xl">
        <div className="p-3 bg-neutral-900 border-b border-neutral-800 flex items-center justify-between">
          <div className="text-fg font-medium">Notifications</div>
          <button className="text-xs text-fg-2 hover:text-fg" onClick={onMarkAllRead}>Mark all read</button>
        </div>
        <div className="max-h-[60vh] overflow-auto p-2 space-y-2">
          {items.length === 0 ? (
            <div className="text-sm text-fg-3 px-2 py-6 text-center">No notifications.</div>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                className={`rounded-lg border ${n.unread ? 'border-emerald-500/40 bg-emerald-900/10' : 'border-neutral-800 bg-neutral-900/60'} p-3`}
              >
                <div className="flex gap-3">
                  <div className="h-9 w-9 rounded-full overflow-hidden border border-neutral-700 bg-neutral-800 flex items-center justify-center">
                    {n.avatarUrl ? (
                      <img src={n.avatarUrl} alt={n.title} className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-xs text-fg-3">!</span>
                    )}
                  </div>
                  <button className="min-w-0 text-left flex-1" onClick={() => { n.onOpen?.(); onDismiss(n.id); }}>
                    <div className="text-sm text-fg font-medium truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-fg-2 break-words">{n.body}</div>}
                    <div className="text-meta text-fg-3 mt-1">
                      {new Date(n.createdAt).toLocaleString()}
                    </div>
                  </button>
                </div>
                {n.actions && n.actions.length > 0 && (
                  <div className="mt-2 flex gap-2 justify-end">
                    {n.actions.map((a, idx) => (
                      <button
                        key={idx}
                        className={`px-2.5 py-1.5 rounded text-xs border ${
                          a.style === 'danger'
                            ? 'border-red-800 text-red-300 hover:bg-red-900/30'
                            : 'border-emerald-700 bg-emerald-800/70 text-emerald-50 hover:bg-emerald-700/70'
                        }`}
                        onClick={a.onClick}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
